import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Smartphone, 
  Server, 
  Database, 
  Zap, 
  Shield, 
  Rocket,
  Code2,
  Mail,
  MapPin,
  Send,
  CheckCircle2,
  Github,
  Linkedin,
  Twitter,
  ArrowRight,
  Sparkles,
  Phone,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { portfolioProjects, services } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
console.log("API URL:", API);

const iconMap = {
  Smartphone,
  Server,
  Database,
  Zap,
  Shield,
  Rocket
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        setFormSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setFormSubmitted(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(
        error.response?.data?.detail || 
        'Failed to send message. Please try again or contact us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_fullstack-app-build-1/artifacts/r20y679s_WhatsApp%20Sticker%202026-04-07%20at%206.28.14%20PM" 
                alt="Prime Stack Logo" 
                className="h-10 w-auto object-contain"
              />
              <span className="text-xl font-bold text-white">Prime Stack</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-orange-400 transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="text-slate-300 hover:text-orange-400 transition-colors">
                Portfolio
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-orange-400 transition-colors">
                Contact
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              >
                Hire Me
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeInLeft">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Building Powerful
                <span className="block mt-2 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 text-transparent bg-clip-text">
                  Full-Stack Applications
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
                Expert Flutter developer & backend specialist crafting beautiful, scalable applications. 
                From concept to deployment, we deliver complete solutions that drive results.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToSection('contact')}
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-8 group animate-pulse-glow"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  onClick={() => scrollToSection('portfolio')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-slate-700 text-white hover:bg-slate-800 text-lg px-8"
                >
                  View Portfolio
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2 animate-fadeInUp delay-100">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  <span className="text-slate-300">Flutter Expert</span>
                </div>
                <div className="flex items-center gap-2 animate-fadeInUp delay-200">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  <span className="text-slate-300">Backend Specialist</span>
                </div>
                <div className="flex items-center gap-2 animate-fadeInUp delay-300">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  <span className="text-slate-300">Full-Stack</span>
                </div>
              </div>
            </div>

            <div className="relative animate-fadeInRight">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-cyan-500/20 blur-3xl rounded-full animate-float"></div>
              <div className="relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-xl border border-slate-700 hover-lift">
                    <Smartphone className="w-8 h-8 text-cyan-400 mb-3 animate-float" />
                    <div className="text-3xl font-bold text-white mb-1">100%</div>
                    <div className="text-slate-400 text-sm">Client Satisfaction</div>
                  </div>
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-xl border border-slate-700 hover-lift">
                    <Zap className="w-8 h-8 text-orange-400 mb-3 animate-float" style={{animationDelay: '0.5s'}} />
                    <div className="text-3xl font-bold text-white mb-1">24/7</div>
                    <div className="text-slate-400 text-sm">Support Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Services We Offer
            </h2>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
              Comprehensive app development services from frontend to backend, ensuring your vision becomes reality
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-slate-900/50 backdrop-blur-sm border-slate-800 hover:border-orange-500/50 transition-all duration-300 group hover-lift card-entrance" style={{animationDelay: `${service.id * 0.1}s`}}>
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform icon-spin-hover">
                      <IconComponent className="w-7 h-7 text-orange-400" />
                    </div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-slate-400">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300">
                          <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Featured Projects
            </h2>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
              A showcase of full-stack applications we've built with Flutter and modern backend technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.map((project) => (
              <Card key={project.id} className="bg-slate-900/50 backdrop-blur-sm border-slate-800 overflow-hidden hover:border-orange-500/50 transition-all duration-300 group hover-lift card-entrance" style={{animationDelay: `${project.id * 0.1}s`}}>
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-500/90 text-white border-0">
                      {project.category}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-white text-xl">{project.title}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {project.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, idx) => (
                      <Badge key={idx} variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-sm text-slate-500">
                    Duration: {project.duration}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Let's Work Together
            </h2>
            <p className="text-lg md:text-xl text-slate-400">
              Have a project in mind? Get in touch and let's create something amazing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-orange-400" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Email</div>
                      <div className="text-white font-medium">primestack51@gmail.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Phone</div>
                      <div className="text-white font-medium">+91 9373669779</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Location</div>
                      <div className="text-white font-medium">Pune, Maharashtra</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-4">Follow Me</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://github.com/primestack51" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-slate-800 hover:bg-orange-500 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <Github className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>

            <Card className="bg-slate-900/50 backdrop-blur-sm border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Send a Message</CardTitle>
                <CardDescription className="text-slate-400">
                  Fill out the form below and we'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                {formSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-slate-400 text-center">Thank you for reaching out. We'll respond soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-300">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        required
                        minLength={2}
                        disabled={isSubmitting}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-300">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        required
                        disabled={isSubmitting}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-slate-300">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Project inquiry"
                        required
                        minLength={5}
                        disabled={isSubmitting}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-slate-300">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your project..."
                        rows={5}
                        required
                        minLength={10}
                        disabled={isSubmitting}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 resize-none disabled:opacity-50"
                      />
                    </div>
                    
                    {submitError && (
                      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <p className="text-red-400 text-sm">{submitError}</p>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_fullstack-app-build-1/artifacts/r20y679s_WhatsApp%20Sticker%202026-04-07%20at%206.28.14%20PM" 
              alt="Prime Stack Logo" 
              className="h-8 w-auto object-contain"
            />
            <span className="text-white font-semibold">Prime Stack</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
